// store.js
import { createStore } from 'vuex';
import apiClient from '@/axios';
import router from '@/router';

const store = createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || '',
    roleLoaded: false,
    role: localStorage.getItem('role') || 'admin', // Default to admin for admin portal
    talents: [],
    projects: [],
    users: [],
    availableRoles: JSON.parse(localStorage.getItem('availableRoles')) || ['admin'],
    notification: {
      show: false,
      type: '',
      message: '',
      duration: 5000
    }
  },
  getters: {
    isAdmin: (state) => state.role === 'admin',
    roleLoaded: (state) => state.roleLoaded,
    isAuthenticated: (state) => !!state.token && !!state.user,
    notification: (state) => state.notification,
    hasNotification: (state) => state.notification.show,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      if (user && user.role && Array.isArray(user.role)) {
        state.availableRoles = user.role;
      } else {
        state.availableRoles = ['admin'];
      }
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('availableRoles', JSON.stringify(state.availableRoles));
    },
    setToken(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },
    setRole(state, role) {
      state.role = role;
      state.roleLoaded = true;
      localStorage.setItem('role', role);
    },
    clearUser(state) {
      state.user = null;
      state.token = '';
      state.roleLoaded = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    logout(state) {
      state.user = null;
      state.token = '';
      state.role = 'admin';
      state.roleLoaded = false;
      state.talents = [];
      state.projects = [];
      state.users = [];
      state.availableRoles = ['admin'];
    
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('user');
      localStorage.removeItem('availableRoles');
    },
    setAvailableRoles(state, roles) {
      state.availableRoles = roles;
      localStorage.setItem('availableRoles', JSON.stringify(roles));
    },
    setRoleLoaded(state, loaded) {
      state.roleLoaded = loaded;
    },
    clearRoleData(state) {
      state.talents = [];
      state.projects = [];
      state.users = [];
    },
    setTalents(state, talents) {
      state.talents = talents;
    },
    setProjects(state, projects) {
      state.projects = projects;
    },
    setUsers(state, users) {
      state.users = users;
    },
    showNotification(state, { type, message, duration = 5000 }) {
      state.notification = {
        show: true,
        type,
        message,
        duration
      };
    },
    hideNotification(state) {
      state.notification = {
        show: false,
        type: '',
        message: '',
        duration: 5000
      };
    },
    syncFromLocalStorage(state) {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      const availableRoles = localStorage.getItem('availableRoles');

      state.user = user ? JSON.parse(user) : null;
      state.token = token || '';
      state.role = role || 'admin';
      state.availableRoles = availableRoles ? JSON.parse(availableRoles) : ['admin'];
      
      if (!state.user || !state.token) {
        state.roleLoaded = false;
        state.talents = [];
        state.projects = [];
        state.users = [];
      }
    }
  },
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await apiClient.post('api/token', credentials);
        commit('setToken', response.data.access_token);
        commit('setUser', response.data.user);

        if (response.data.user && response.data.user.role && response.data.user.role.includes('admin')) {
          commit('setRole', 'admin');
        } else {
          throw new Error('Unauthorized: Admin access required');
        }

        commit('setRoleLoaded', true);
        
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'login', user: response.data.user } 
        }));
        
        router.push({ name: 'Dashboard' });
      } catch (error) {
        console.error('Error logging in:', error);
        throw error;
      }
    },

    async logout({ commit }) {
      try {
        commit('logout');
        
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'logout' } 
        }));
        
        router.push({ name: 'Login' });
      } catch (error) {
        console.error('Error logging out:', error);
        commit('logout');
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'logout' } 
        }));
        router.push({ name: 'Login' });
      }
    },

    async fetchRoleData({ commit, state }) {
      try {
        if (!state.token || !state.user) {
          return false;
        }

        const talentsResponse = await apiClient.get('/web/talents');
        commit('setTalents', talentsResponse.data.data);

        const projectsResponse = await apiClient.get('/web/projects_all');
        commit('setProjects', projectsResponse.data.data);

        const usersResponse = await apiClient.get('web/users');
        commit('setUsers', usersResponse.data.data);

        return true;
      } catch (error) {
        console.error('Error fetching role data:', error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          commit('logout');
          window.dispatchEvent(new CustomEvent('auth-state-changed', { 
            detail: { type: 'logout' } 
          }));
        }
        return false;
      }
    },

    async initializeStore({ commit, dispatch, state }) {
      commit('syncFromLocalStorage');
      
      if (state.user && state.token) {
        const success = await dispatch('fetchRoleData');
        if (success) {
          commit('setRoleLoaded', true);
        }
      } else {
        commit('setRoleLoaded', true);
      }
    },

    handleStorageChange({ commit, dispatch }, event) {
      if (event.key === 'user' || event.key === 'token' || event.key === 'role' || event.key === 'availableRoles') {
        commit('syncFromLocalStorage');
        dispatch('initializeStore');
      }
    },
    showErrorNotification({ commit }, message) {
      commit('showNotification', { type: 'error', message });
    },
    
    showSuccessNotification({ commit }, message) {
      commit('showNotification', { type: 'success', message });
    },
    
    showWarningNotification({ commit }, message) {
      commit('showNotification', { type: 'warning', message });
    },
    
    showInfoNotification({ commit }, message) {
      commit('showNotification', { type: 'info', message });
    },
    
    clearNotification({ commit }) {
      commit('hideNotification');
    }
  }
});

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    store.dispatch('handleStorageChange', event);
  });

  window.addEventListener('auth-state-changed', (event) => {
    console.log('Auth state changed in another tab:', event.detail);
    store.commit('syncFromLocalStorage');
    store.dispatch('initializeStore');
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      store.commit('syncFromLocalStorage');
      store.dispatch('initializeStore');
    }
  });

  window.addEventListener('focus', () => {
    store.commit('syncFromLocalStorage');
    store.dispatch('initializeStore');
  });
}

export default store;
